import React, { useState, useRef, useEffect } from 'react';
import Carousel from 'react-spring-3d-carousel';
import { config } from 'react-spring';
import { v4 as uuidv4 } from 'uuid';
import { FaBuilding, FaTrain, FaSeedling, FaMountain, FaRoad } from 'react-icons/fa';

const services = [
  {
    icon: FaBuilding,
    title: 'General Contracting',
    titleAr: 'المقاولات العامة',
    desc: 'We offer full-scale contracting services for all project types.',
    descAr: 'نقدم خدمات مقاولات شاملة لجميع أنواع المشاريع.'
  },
  {
    icon: FaSeedling,
    title: 'Expansion Projects',
    titleAr: 'مشاريع الاستصلاح والتوسعة',
    desc: 'Expansion and reclamation for sustainable growth.',
    descAr: 'مشاريع استصلاح وتوسعة لتحقيق التنمية المستدامة.'
  },
  {
    icon: FaMountain,
    title: 'Quarry Management',
    titleAr: 'إدارة وتشغيل المحاجر',
    desc: 'Operate and manage quarries with high efficiency.',
    descAr: 'إدارة وتشغيل المحاجر بكفاءة عالية.'
  },
  {
    icon: FaTrain,
    title: 'Railways',
    titleAr: 'سكك حديدية',
    desc: 'Specialists in railway construction and maintenance.',
    descAr: 'متخصصون في إنشاء وصيانة السكك الحديدية.'
  },
  {
    icon: FaRoad,
    title: 'Road Works',
    titleAr: 'أعمال الطرق',
    desc: 'Design and build modern, durable road networks.',
    descAr: 'تصميم وتنفيذ شبكات طرق حديثة ومتينة.'
  },
];

const useOffsetRadius = () => {
  const getOffset = () => (window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 2);
  const [offsetRadius, setOffsetRadius] = useState(getOffset());
  useEffect(() => {
    const handleResize = () => setOffsetRadius(getOffset());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return offsetRadius;
};

interface ServiceCardProps {
  service: typeof services[0];
  isActive: boolean;
  isRTL: boolean;
  language: 'ar' | 'en';
}
const ServiceCard: React.FC<ServiceCardProps> = ({ service, isActive, isRTL, language }) => (
  <div
    className={`relative flex flex-col items-center justify-center rounded-3xl transition-all duration-500 px-8 py-12 mx-auto select-none overflow-hidden ${
      isActive
        ? 'scale-110 z-20 opacity-100'
        : 'scale-95 opacity-60'
    } min-w-[280px] max-w-[400px] h-[380px]`}
    style={{
      background: isActive 
        ? 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)'
        : 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.7) 100%)',
      direction: isRTL ? 'rtl' : 'ltr',
      backdropFilter: 'blur(20px)',
      border: isActive ? '2px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255,255,255,0.2)',
      boxShadow: isActive 
        ? '0 25px 50px -12px rgba(59, 130, 246, 0.4), 0 0 0 1px rgba(59, 130, 246, 0.1)'
        : '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255,255,255,0.1)',
    }}
    tabIndex={0}
    aria-label={language === 'ar' ? service.titleAr : service.title}
  >
    {/* Cinematic Glow Effect */}
    {isActive && (
      <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 via-purple-400/20 to-pink-400/20 rounded-3xl animate-pulse"></div>
    )}
    
    <div className="relative z-10 flex flex-col items-center justify-center h-full">
      <div className="flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 shadow-2xl mb-8 transform transition-transform duration-300 hover:scale-110">
        {service.icon ? (
          React.createElement(service.icon, { className: 'w-12 h-12 text-white' })
        ) : null}
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
        {language === 'ar' ? service.titleAr : service.title}
      </h3>
      <p className="text-gray-700 text-center text-base mb-4 line-clamp-3 font-light leading-relaxed">
        {language === 'ar' ? service.descAr : service.desc}
      </p>
    </div>
  </div>
);

interface ServicesSectionProps {
  language: 'ar' | 'en';
}

const ServicesSection: React.FC<ServicesSectionProps> = ({ language }) => {
  const isRTL = language === 'ar';
  const [goToSlide, setGoToSlide] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const offsetRadius = useOffsetRadius();

  // Drag/Swipe Handlers with standard logic
  let startX: number | null = null, isDragging = false, mouseDownX: number | null = null;
  // Touch
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => { startX = e.touches[0].clientX; isDragging = true; };
  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging || startX === null) return;
    const diff = e.touches[0].clientX - startX;
    if (Math.abs(diff) > 30) { // Wider drag area
      if (diff < 0) setGoToSlide((goToSlide + 1) % services.length); // swipe left: next
      else setGoToSlide((goToSlide - 1 + services.length) % services.length); // swipe right: prev
      isDragging = false; startX = null;
    }
  };
  const handleTouchEnd = () => { isDragging = false; startX = null; };
  // Mouse
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => { mouseDownX = e.clientX; isDragging = true; };
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || mouseDownX === null) return;
    const diff = e.clientX - mouseDownX;
    if (Math.abs(diff) > 30) { // Wider drag area
      if (diff < 0) setGoToSlide((goToSlide + 1) % services.length); // drag left: next
      else setGoToSlide((goToSlide - 1 + services.length) % services.length); // drag right: prev
      isDragging = false; mouseDownX = null;
    }
  };
  const handleMouseUp = () => { isDragging = false; mouseDownX = null; };

  // Prepare slides for the carousel (do NOT reverse for RTL)
  const slides = services.map((service, idx) => ({
    key: uuidv4(),
    content: <ServiceCard service={service} isActive={goToSlide === idx} isRTL={isRTL} language={language} />,
    onClick: () => setGoToSlide(idx),
  }));

  return (
    <section className={`section-padding relative overflow-hidden ${isRTL ? 'font-cairo' : 'font-montserrat'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Cinematic Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900"></div>
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}></div>
      
      {/* Animated Light Effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
      <div className="absolute -bottom-8 left-1/3 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '4s'}}></div>
      
      <div className="container-custom max-w-7xl mx-auto relative z-10">
        {/* Title */}
        <div className="text-center mb-80">
          <h2 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight flex items-center justify-center gap-4">
            {language === 'ar' ? 'خدماتنا' : 'Our Services'}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
              {language === 'ar' ? 'المتميزة' : 'Excellence'}
            </span>
          </h2>
          {/* Description */}
          <p className="text-xl text-gray-300 font-light max-w-4xl mx-auto leading-relaxed">
            {language === 'ar'
              ? 'نقدم مجموعة متكاملة من الخدمات الهندسية والتنفيذية التي تلبي احتياجات مشاريعكم بأعلى معايير الجودة والاحترافية.'
              : 'We provide an integrated set of engineering and executive services that meet your project needs with the highest standards of quality and professionalism.'}
          </p>
        </div>

        {/* Carousel Container */}
        <div className="flex flex-col items-center justify-center w-full max-w-6xl mx-auto mb-16">
          {/* Carousel */}
          <div
            className="w-full max-w-5xl mx-auto select-none cursor-grab mb-64"
            ref={carouselRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <Carousel
              slides={slides}
              goToSlide={goToSlide}
              offsetRadius={offsetRadius}
              showNavigation={false}
              animationConfig={config.gentle}
            />
          </div>

          {/* Navigation Arrows */}
          <div className="flex items-center justify-center space-x-16 md:space-x-16 space-x-14 rtl:space-x-reverse">
            {/* Left Arrow */}
            <button
              className="relative bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full w-12 h-12 md:w-24 md:h-24 flex items-center justify-center shadow-2xl focus:outline-none focus:ring-4 focus:ring-blue-400/50 transition-all duration-500 hover:scale-110 hover:shadow-blue-500/25 backdrop-blur-sm border border-white/20"
              onClick={() => setGoToSlide((goToSlide + 1) % services.length)}
              aria-label={isRTL ? 'السابق' : 'Previous'}
            >
              <span className="text-4xl md:text-7xl font-bold drop-shadow-lg">‹</span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full animate-pulse"></div>
            </button>

            {/* Dots Indicator */}
            <div className="flex space-x-4 md:space-x-6 rtl:space-x-reverse">
              {services.map((_, idx) => (
                <button
                  key={idx}
                  className={`w-4 h-4 md:w-6 md:h-6 rounded-full transition-all duration-500 ${
                    goToSlide === idx 
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 scale-150 shadow-lg shadow-blue-500/50' 
                      : 'bg-white/30 hover:bg-white/50 backdrop-blur-sm border border-white/20'
                  }`}
                  onClick={() => setGoToSlide(idx)}
                  aria-label={`${isRTL ? 'انتقل إلى' : 'Go to'} ${idx + 1}`}
                />
              ))}
            </div>

            {/* Right Arrow */}
            <button
              className="relative bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-full w-12 h-12 md:w-24 md:h-24 flex items-center justify-center shadow-2xl focus:outline-none focus:ring-4 focus:ring-purple-400/50 transition-all duration-500 hover:scale-110 hover:shadow-purple-500/25 backdrop-blur-sm border border-white/20"
              onClick={() => setGoToSlide((goToSlide - 1 + services.length) % services.length)}
              aria-label={isRTL ? 'التالي' : 'Next'}
            >
              <span className="text-4xl md:text-7xl font-bold drop-shadow-lg">›</span>
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
